// domain/services/holdReasonAudience.js

/**
 * Which of the projector's hold reasons a customer should ever be shown, and what to say instead.
 *
 * THE PROJECTOR'S VOCABULARY IS A MEASUREMENT ONE. It was built to answer "what is stopping this
 * organization from billing, ranked", which is a question we ask of the data, not language for a
 * biller. Some of it is actionable once translated. Some of it describes a state that should be
 * impossible, and telling a customer their charge master is "ambiguous" invites them to go and fix
 * something only we can.
 *
 * SO THERE ARE EXACTLY TWO AUDIENCES. A curated subset gets its own copy, naming the thing to go and
 * change. Everything else — including any reason added to the projector after this file was written
 * — collapses into one "needs attention" that routes to support. UNKNOWN FALLS TO SUPPORT ON
 * PURPOSE: a new reason reaching a customer as a raw enum is worse than one reaching them as a
 * referral, and this way adding a reason on the backend can never leak a string nobody wrote.
 *
 * THE LIST IS A PRODUCT DECISION, not an engineering one. It is here because it has to live
 * somewhere testable; which reasons belong in it is for product and design to settle, and moving a
 * reason between the two halves is a copy change rather than a code change.
 */

/**
 * The reasons we explain in a customer's own words, in the order to act on them.
 *
 * NAMED FOR WHAT IT IS rather than "actionable", because one member is not. A payer billed by
 * invoice or roster holds every item and there is nothing a clinic can do about it — but telling
 * them so is far better than a support referral for a feature request, so it gets copy and sits
 * near the end, below everything somebody could actually go and fix.
 *
 * THE ORDER IS WHAT `primaryHoldReason` RESOLVES BY: who pays before what it costs, because a
 * coverage decides which charge applies.
 */
export const EXPLAINED_HOLD_REASONS = [
  'no_coverage',
  'coverage_unverified',
  'coverage_on_hold',
  'coverage_policyholder_incomplete',
  'coverage_policyholder_unresolved',
  'patient_demographics_incomplete',
  // Fixed on the client record, like the two above it — and dated, so the copy has to say so: a
  // diagnosis recorded today does not reach a session delivered before it.
  'no_diagnosis',
  'no_funder_as_of',
  'incomplete_funder_config',
  // The payer's conditional modifier rules, in the order somebody would work through them: the
  // payer having answered comes before the payer having said nothing, because "this tier is not
  // paid" sends somebody to reassign the session while the other sends them back to the rules.
  'tier_not_payable',
  'no_modifier_tier',
  'too_many_modifiers',
  'held_for_auth',
  'no_treatment_service',
  'duplicate_treatment_service',
  'no_code_source',
  'not_billable',
  'code_not_valid_on_date',
  'manually_entered_code',
  'no_place_of_service',
  'no_charge_master',
  'no_charge',
  'no_billing_provider_npi',
  'no_rendering_provider',
  // Fixed on the person's profile rather than on the payer, which is why it sits with the rendering
  // provider rather than with the three modifier reasons above.
  'performer_qualifications_unknown',
  // Nothing to fix, but worth saying: see the note above.
  'unsupported_channel',
];

/**
 * What everything else becomes. Not a reason the projector produces: a bucket, so the screen never
 * has to render one of ours.
 *
 * IT MEANS "ONLY WE CAN FIX THIS", and nothing else belongs in it. Three reasons qualify: a clinic
 * holding two charge master series, which even the editor refuses to show; a managed catalog record
 * carrying no unit basis, which is ours; and an outright defect. Everything a customer could act on
 * has its own copy, because sending somebody to support for something they could have fixed
 * themselves is the worst of both outcomes.
 *
 * THAT LAST SENTENCE IS EASY TO BREAK FROM THE OTHER SIDE OF THE STACK, because the default for a
 * reason nobody classified is the support referral. `backendContract` pins this file against the
 * projector's vocabulary so a new reason fails the build instead of quietly landing here.
 */
export const NEEDS_ATTENTION = 'needs_attention';

/**
 * The reasons we have DECIDED belong to support, as opposed to the ones that merely land there.
 *
 * NOT CONTROL FLOW, and it must never become it: `customerFacingReason` still routes anything it
 * does not recognize to support, which is what stops an unwritten reason reaching a customer as a
 * raw enum. This list is the other half of the product decision written down — the register of
 * reasons somebody looked at and judged ours alone.
 *
 * ITS JOB IS TO FAIL THE BUILD. `backendContract` asserts these two lists together cover the
 * projector's whole vocabulary, so a new reason lands in neither and the suite says so.
 */
export const SUPPORT_ONLY_HOLD_REASONS = [
  // Two charge master series on one clinic. The editor refuses to show it; nothing produces it.
  'ambiguous_charge_master',
  // Two staff records claiming one login, which only a relink that left the old record behind
  // produces. Ours because settling it means merging two profiles, not correcting a field.
  'ambiguous_performer_identity',
  // A managed catalog record with no unit basis of its own. The catalog is ours.
  'no_unit_basis',
  'unexpected_error',
];

const EXPLAINED = new Set(EXPLAINED_HOLD_REASONS);

/**
 * The reason to show for a hold, which is not always the reason that was recorded.
 *
 * @param {string} holdReason - as the projector recorded it
 * @returns {string} an actionable reason, or NEEDS_ATTENTION
 */
export function customerFacingReason(holdReason) {
  return EXPLAINED.has(holdReason) ? holdReason : NEEDS_ATTENTION;
} // customerFacingReason()

/**
 * The one reason to put on a row that holds for several.
 *
 * FIRST IN THE CURATED ORDER, rather than first as recorded. An item held for both a missing charge
 * and an unverified coverage is recorded in resolver order, which is an implementation detail of the
 * chain; showing whichever happened to run first would make two identical problems read differently.
 * The order here is the order a clinic can act in: who pays before what it costs, because a coverage
 * decides which charge applies.
 *
 * NEEDS_ATTENTION NEVER WINS while anything explained is present. A row that says "contact support"
 * while also missing a payer sends somebody to us for something they could have fixed themselves.
 *
 * @param {string[]} holdReasons
 * @returns {string|null} null when the item is not held at all
 */
export function primaryHoldReason(holdReasons) {
  if (!Array.isArray(holdReasons) || holdReasons.length === 0) {
    return null;
  }
  const explained = EXPLAINED_HOLD_REASONS.find((reason) => holdReasons.includes(reason));

  return explained ?? NEEDS_ATTENTION;
} // primaryHoldReason()
